import { useCallback, useEffect, useMemo, useState } from 'react'
import { FilterBar } from '../components/common/FilterBar'
import { ShieldAlert, AlertCircle, Wrench, Receipt } from 'lucide-react'
import { useGetMaintenanceMonths, useGetFleet } from '../hooks/backend/fleet'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Label } from '../lib/shadcn/label'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import { RecordMaintenanceDialog } from '../components/fleet/RecordMaintenanceDialog'
import { MaintenanceBreakdownDialog, type BreakdownFilter } from '../components/fleet/MaintenanceBreakdownDialog'
import type { FleetRow } from '../utils/fleet'
import { type BorneBy, type MaintenanceMonthRow, monthLabel, monthsAgo, zar } from '../utils/servicing'

type Grouping = 'month' | 'vehicle'

const CATEGORY_COLUMNS: { key: keyof MaintenanceMonthRow; label: string; code: string }[] = [
  { key: 'service_ex_vat_zar', label: 'Service', code: 'service' },
  { key: 'tyres_ex_vat_zar', label: 'Tyres', code: 'tyres' },
  { key: 'brakes_ex_vat_zar', label: 'Brakes', code: 'brakes' },
  { key: 'roadworthy_ex_vat_zar', label: 'Roadworthy', code: 'roadworthy' },
  { key: 'panel_beating_ex_vat_zar', label: 'Panel beating', code: 'panel_beating' },
  { key: 'mechanical_ex_vat_zar', label: 'Mechanical', code: 'mechanical' },
  { key: 'other_ex_vat_zar', label: 'Other', code: 'other' },
  { key: 'fleetly_ex_vat_zar', label: 'Fleetly cost', code: 'fleetly_cost' },
]

// Who carries the invoiced total. The three always sum to it when known, and
// all three are withheld together without finance (getMaintenanceMonths).
const PAYER_COLUMNS: { key: keyof MaintenanceMonthRow; label: string; borne: BorneBy }[] = [
  { key: 'owner_charge_zar', label: 'Owner', borne: 'owner' },
  { key: 'driver_charge_zar', label: 'Driver', borne: 'driver' },
  { key: 'fleetly_borne_zar', label: 'Fleetly', borne: 'fleetly' },
]

function num(v: unknown): number | null {
  if (v === null || v === undefined) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/** A figure that opens what makes it up. A zero has nothing behind it and stays plain. */
function Figure({ value, onOpen, strong, title }: {
  value: number | null; onOpen: () => void; strong?: boolean; title?: string
}) {
  if (value === null) return <span className="text-muted-foreground">—</span>
  if (value === 0) return <span className="text-muted-foreground/50">—</span>
  return (
    <button type="button" onClick={onOpen} title={title ?? 'See what makes up this figure'}
      className={cn('tabular-nums underline decoration-dotted decoration-muted-foreground/40 underline-offset-2 hover:text-[hsl(var(--brand))] hover:decoration-[hsl(var(--brand))]',
        strong ? 'font-semibold text-foreground' : 'text-foreground')}>
      {zar(value)}
    </button>
  )
}

/**
 * Vehicle servicing and repairs.
 *
 * Every figure on this page is summed by Postgres in v_vehicle_maintenance_month
 * and the owner-borne slice is decided by maintenance_charge_rules, so changing
 * what an owner carries is a database row and never a change here. That is not
 * fastidiousness: the workbook this replaces recomputed the owner charge a
 * different way in six of its ten months, and the difference between the two
 * readings across November 2025 to August 2026 is R36,998.
 *
 * Amounts are shown excluding VAT with VAT in its own column, because the
 * workbook's own VAT columns are amount times 0.15, which is what proves its
 * category figures were VAT-exclusive all along.
 */
export default function Servicing() {
  const { loading: accessLoading, canView, canEditFleet, canViewFinance, caption } = useCapabilities()
  const [grouping, setGrouping] = useState<Grouping>('month')
  const [from, setFrom] = useState(monthsAgo(11).slice(0, 7))
  const [to, setTo] = useState(monthsAgo(0).slice(0, 7))
  const [search, setSearch] = useState('')
  const [drill, setDrill] = useState<BreakdownFilter | null>(null)

  const months = useGetMaintenanceMonths()
  const fleet = useGetFleet()
  const trigger = months.trigger
  const fleetTrigger = fleet.trigger

  const load = useCallback(() => {
    trigger({ period_from: `${from}-01`, period_to: `${to}-01` }, { skipCache: true })
  }, [trigger, from, to])

  useEffect(() => {
    if (canView) {
      load()
      fleetTrigger({ statuses: ['active', 'contracting', 'offboarding'], location: null, operational: null })
    }
  }, [canView, load, fleetTrigger])

  const allRows = (months.data as MaintenanceMonthRow[] | undefined) ?? []
  const vehicles = (fleet.data as FleetRow[] | undefined) ?? []

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return allRows
    return allRows.filter((r) =>
      [r.registration_number, r.owner_name, r.make, r.model]
        .filter(Boolean).join(' ').toLowerCase().includes(term),
    )
  }, [allRows, search])

  const totals = useMemo(() => {
    const t = {
      ex_vat: 0, vat: 0, incl: 0, owner: 0, ownerKnown: false, lines: 0,
      driver: 0, fleetly: 0, unnamed: 0,
      byCategory: {} as Record<string, number>,
    }
    for (const r of rows) {
      t.ex_vat += Number(r.total_ex_vat_zar ?? 0)
      t.vat += Number(r.total_vat_zar ?? 0)
      t.incl += Number(r.total_incl_vat_zar ?? 0)
      t.lines += r.line_count ?? 0
      if (r.owner_charge_zar !== null && r.owner_charge_zar !== undefined) {
        t.owner += Number(r.owner_charge_zar)
        t.ownerKnown = true
      }
      t.driver += Number(r.driver_charge_zar ?? 0)
      t.fleetly += Number(r.fleetly_borne_zar ?? 0)
      t.unnamed += Number(r.driver_unnamed_count ?? 0)
      for (const c of CATEGORY_COLUMNS) {
        t.byCategory[c.label] = (t.byCategory[c.label] ?? 0) + Number(r[c.key] ?? 0)
      }
    }
    return t
  }, [rows])

  // Grouped for display. The group key is the only thing that changes between
  // the two views; the aggregation is identical, so the two can never disagree.
  const groups = useMemo(() => {
    const map = new Map<string, { label: string; rows: MaintenanceMonthRow[] }>()
    for (const r of rows) {
      const key = grouping === 'month' ? r.period_start : (r.registration_number ?? r.vehicle_id)
      const label = grouping === 'month' ? monthLabel(r.period_start) : (r.registration_number ?? 'Unknown vehicle')
      if (!map.has(key)) map.set(key, { label, rows: [] })
      map.get(key)!.rows.push(r)
    }
    const arr = [...map.entries()].map(([key, v]) => ({ key, ...v }))
    arr.sort((a, b) => (grouping === 'month' ? b.key.localeCompare(a.key) : a.key.localeCompare(b.key)))
    return arr
  }, [rows, grouping])

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            Servicing and repairs is available to admins only.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <Wrench className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Servicing and repairs</h1>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground tabular-nums">
              {months.loading ? '…' : `${rows.length} vehicle month${rows.length === 1 ? '' : 's'}`}
            </span>
            {canEditFleet && (
              <RecordMaintenanceDialog vehicles={vehicles} onSaved={load} />
            )}
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>

        <div className="mt-3 flex flex-wrap gap-2">
          <Chip tone="neutral">
            Excluding VAT
            <span className="ml-1 font-semibold tabular-nums">{zar(totals.ex_vat)}</span>
          </Chip>
          <Chip tone="neutral">
            VAT
            <span className="ml-1 font-semibold tabular-nums">{zar(totals.vat)}</span>
          </Chip>
          <Chip tone="brand">
            Invoiced total
            <span className="ml-1 font-semibold tabular-nums">{zar(totals.incl)}</span>
          </Chip>
          {canViewFinance ? (
            PAYER_COLUMNS.map((p) => {
              const v = p.borne === 'owner' ? (totals.ownerKnown ? totals.owner : null)
                : p.borne === 'driver' ? totals.driver : totals.fleetly
              return (
                <button key={p.borne} type="button" disabled={v === null}
                  onClick={() => setDrill({ title: `${p.label} borne, ${from} to ${to}`, period_from: `${from}-01`, period_to: `${to}-01`, borne_by: p.borne })}>
                  <Chip tone={p.borne === 'owner' ? 'amber' : p.borne === 'driver' ? 'red' : 'brand'}>
                    {p.label} borne
                    <span className="ml-1 font-semibold tabular-nums underline decoration-dotted underline-offset-2">{v === null ? '—' : zar(v)}</span>
                  </Chip>
                </button>
              )
            })
          ) : (
            <Chip tone="neutral">Owner, driver and Fleetly borne withheld, needs finance access</Chip>
          )}
          {totals.unnamed > 0 && (
            <button type="button"
              onClick={() => setDrill({ title: `Driver borne lines, ${from} to ${to}`, period_from: `${from}-01`, period_to: `${to}-01`, borne_by: 'driver' })}>
              <Chip tone="red">{totals.unnamed} driver borne line{totals.unnamed === 1 ? '' : 's'} with no driver named</Chip>
            </button>
          )}
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          Click any figure to see the lines and invoices behind it, move a line to another category, or say who pays.
          Panel beating is driver borne and mechanical is owner borne unless a line says otherwise; driver borne opens
          a repair case and deducts nothing on its own.
        </p>
      </div>

      <FilterBar
        activeCount={(from ? 1 : 0) + (to ? 1 : 0) + (search.trim() ? 1 : 0)}
        summary={[
          from || to ? `${from || 'start'} to ${to || 'now'}` : null,
          `by ${grouping}`,
          search.trim() ? `"${search.trim()}"` : null,
        ]
          .filter(Boolean)
          .join(', ')}
      >
      <div className="flex flex-wrap items-end gap-3 p-3">
        <div className="space-y-1">
          <Label htmlFor="sv-from" className="text-xs">From</Label>
          <Input id="sv-from" type="month" className="w-[150px]" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="sv-to" className="text-xs">To</Label>
          <Input id="sv-to" type="month" className="w-[150px]" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="sv-group" className="text-xs">Group by</Label>
          <NativeSelect id="sv-group" className="w-[150px]" value={grouping}
            onChange={(e) => setGrouping(e.target.value as Grouping)}>
            <option value="month">Month</option>
            <option value="vehicle">Vehicle</option>
          </NativeSelect>
        </div>
        <div className="min-w-[220px] flex-1 space-y-1">
          <Label htmlFor="sv-search" className="text-xs">Search</Label>
          <Input id="sv-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Registration, owner, make or model" />
        </div>
      </div>
      </FilterBar>

      <div className="min-h-0 flex-1 overflow-auto">
        {months.error ? (
          <div className="m-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{months.error}</span>
          </div>
        ) : months.loading && rows.length === 0 ? (
          <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading the maintenance ledger…
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-center text-sm text-muted-foreground">
            <Receipt className="h-6 w-6" />
            No servicing or repair spend recorded in this window.
          </div>
        ) : (
          <div className="min-w-[1180px]">
            {groups.map((g) => {
              const gt = g.rows.reduce(
                (acc, r) => {
                  acc.ex += Number(r.total_ex_vat_zar ?? 0)
                  acc.vat += Number(r.total_vat_zar ?? 0)
                  acc.incl += Number(r.total_incl_vat_zar ?? 0)
                  if (r.owner_charge_zar !== null && r.owner_charge_zar !== undefined) {
                    acc.owner += Number(r.owner_charge_zar)
                    acc.ownerKnown = true
                  }
                  return acc
                },
                { ex: 0, vat: 0, incl: 0, owner: 0, ownerKnown: false },
              )
              const firstRow = g.rows[0]
              const groupFilter = (extra: Partial<BreakdownFilter>, what: string): BreakdownFilter => (
                grouping === 'month'
                  ? { title: `${what}, ${g.label}`, period_from: firstRow.period_start, period_to: firstRow.period_start, ...extra }
                  : { title: `${what}, ${g.label}, ${from} to ${to}`, vehicle_id: firstRow.vehicle_id, period_from: `${from}-01`, period_to: `${to}-01`, ...extra }
              )
              return (
                <div key={g.key}>
                  <div className="sticky top-0 z-10 flex items-baseline gap-3 border-b border-border bg-muted px-3 py-2">
                    <span className="text-sm font-semibold text-foreground">{g.label}</span>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {g.rows.length} {grouping === 'month' ? 'vehicle' : 'month'}
                      {g.rows.length === 1 ? '' : 's'}
                    </span>
                    <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                      {zar(gt.ex)} ex VAT,{' '}
                      <button type="button" className="underline decoration-dotted underline-offset-2 hover:text-[hsl(var(--brand))]"
                        onClick={() => setDrill(groupFilter({}, 'Everything'))}>
                        {zar(gt.incl)} invoiced
                      </button>
                      {canViewFinance && gt.ownerKnown ? `, ${zar(gt.owner)} owner borne` : ''}
                    </span>
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-1.5 text-left font-semibold">
                          {grouping === 'month' ? 'Vehicle' : 'Month'}
                        </th>
                        <th className="px-3 py-1.5 text-left font-semibold">Owner</th>
                        {CATEGORY_COLUMNS.map((c) => (
                          <th key={c.label} className="px-2 py-1.5 text-right font-semibold">{c.label}</th>
                        ))}
                        <th className="px-2 py-1.5 text-right font-semibold">Ex VAT</th>
                        <th className="px-2 py-1.5 text-right font-semibold">VAT</th>
                        <th className="px-2 py-1.5 text-right font-semibold">Invoiced</th>
                        {PAYER_COLUMNS.map((p) => (
                          <th key={p.borne} className="px-2 py-1.5 text-right font-semibold">{p.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {g.rows.map((r) => {
                        const cell = (extra: Partial<BreakdownFilter>, what: string): BreakdownFilter => ({
                          title: `${what}, ${r.registration_number ?? 'car'}, ${monthLabel(r.period_start)}`,
                          vehicle_id: r.vehicle_id, period_from: r.period_start, period_to: r.period_start, ...extra,
                        })
                        return (
                        <tr key={`${r.vehicle_id}-${r.period_start}`} className="border-b border-border/60">
                          <td className="whitespace-nowrap px-3 py-1.5 font-mono font-semibold uppercase text-foreground">
                            <button type="button" className="uppercase hover:text-[hsl(var(--brand))]"
                              onClick={() => setDrill(cell({}, 'Everything'))}>
                              {grouping === 'month' ? (r.registration_number ?? '—') : monthLabel(r.period_start)}
                            </button>
                            {Number(r.driver_unnamed_count ?? 0) > 0 && (
                              <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-rose-500 align-middle"
                                title="A driver borne line here has no driver named" />
                            )}
                            {r.statement_issued && (
                              <span className="ml-1 align-middle font-sans text-[9px] font-normal normal-case text-muted-foreground"
                                title="The owner statement for this car and month has been issued; a change here does not alter it">
                                issued
                              </span>
                            )}
                          </td>
                          <td className="max-w-[180px] truncate px-3 py-1.5 text-muted-foreground">
                            {r.owner_name ?? '—'}
                          </td>
                          {CATEGORY_COLUMNS.map((c) => (
                            <td key={c.label} className="px-2 py-1.5 text-right">
                              <Figure value={num(r[c.key]) ?? 0}
                                onOpen={() => setDrill(cell({ category: c.code }, c.label))} />
                            </td>
                          ))}
                          <td className="px-2 py-1.5 text-right tabular-nums text-foreground">
                            {zar(r.total_ex_vat_zar)}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                            {zar(r.total_vat_zar)}
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <Figure strong value={num(r.total_incl_vat_zar)}
                              onOpen={() => setDrill(cell({}, 'Everything'))} />
                          </td>
                          {PAYER_COLUMNS.map((p) => (
                            <td key={p.borne} className="px-2 py-1.5 text-right">
                              <Figure strong={p.borne === 'owner'} value={num(r[p.key])}
                                onOpen={() => setDrill(cell({ borne_by: p.borne }, `${p.label} borne`))} />
                            </td>
                          ))}
                        </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {!canViewFinance && rows.length > 0 && (
        <div className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
          The owner, driver and Fleetly columns show an em dash because your login does not include finance access.
          That is a withheld figure, not a zero.
        </div>
      )}

      <MaintenanceBreakdownDialog filter={drill} canEdit={canEditFleet}
        onOpenChange={(open) => { if (!open) setDrill(null) }} />
    </div>
  )
}
